// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { mergeWithCss } from "@stoneage/XDL/Utilities/mergeWithCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    email: string;
    extendedStyles?: EmailWithTooltipStyles;
}

export interface EmailWithTooltipStyles {
    container?: TypographyPresentationProps;
}

const emailWithTooltipStyles: EmailWithTooltipStyles = {
    container: {
        forwardAs: "span",
        ellipsis: true,
        css: css`
            display: block;
        `,
    },
};

const EmailWithTooltip: React.FC<OwnProps> = ({ email, extendedStyles, ...otherProps }) => {
    const [styles] = useState(() => mergeWithCss(emailWithTooltipStyles, extendedStyles));

    const mouseOverHandler = (event: React.SyntheticEvent) => {
        const target: any = event.target;
        if (target?.hasAttribute("title")) {
            target?.removeAttribute("title");
        }
    };

    return (
        <Typography
            {...styles.container}
            data-test-selector="email-with-tooltip"
            {...otherProps}
            onMouseOver={mouseOverHandler}
        >
            <Tooltip
                trigger="mouseenter focus"
                triggerComponent={<span>{email}</span>}
                cssOverrides={{
                    tooltipBody: css``,
                    tooltipContainer: css`
                        padding: 8px;
                        overflow: hidden;
                        white-space: pre-wrap;
                        box-shadow: 2px 2px 10px 1px rgba(0, 0, 0, 0.2);
                    `,
                }}
                passAsChildren={true}
            >
                <StyledWrapper
                    css={css`
                        &,
                        p {
                            font-size: 14px;
                        }
                    `}
                >
                    <a href={`mailto:${email}`}>{email}</a>
                </StyledWrapper>
            </Tooltip>
        </Typography>
    );
};

export default EmailWithTooltip;
